import type { Media, MediaFocus } from '../types/media'

/** Hoogstens zoveel tekens beschrijving (ook de server, LARAVEL.md §29). */
export const MEDIA_ALT_MAX = 160
/** Breedtes die de server van elke foto maakt; de site kiest met `srcset`. */
export const MEDIA_WIDTHS = [480, 800, 1200, 1600, 2400] as const
/** Hoogstens zoveel extra beelden per product (naast de packshot). */
export const PRODUCT_MEDIA_MAX = 8

const SAFE_SRC = /^(https:\/\/|http:\/\/localhost|data:image\/|blob:|\/(?!\/))/i
const SAFE_VIDEO = /^(https:\/\/|http:\/\/localhost|blob:|\/(?!\/))/i
/** Een foto uit de eigen bibliotheek: `https://<cdn>/m/<uuid>/<breedte>.webp`. */
const LIBRARY = /^(https:\/\/[^/]+\/m\/[0-9a-f-]{36}\/)\d+\.webp$/i
const ALLOWED_KEYS = new Set(['image', 'alt', 'focus', 'video'])

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

export const emptyMedia = (): Media => ({ image: '', alt: '' })

/** Is er echt beeld? Lege of ontbrekende media = de standaardfoto van die plek. */
export const hasMedia = (m: Partial<Media> | null | undefined): m is Media => typeof m?.image === 'string' && m.image.trim() !== ''

/** De eigen media, of de standaardfoto als die (nog) leeg is. */
export const mediaOr = (m: Partial<Media> | null | undefined, fallback: Media): Media => (hasMedia(m) ? m : fallback)

/**
 * Herkent een `Media`-object in willekeurige inhoud (het beheer bouwt formulieren uit de vorm van
 * de data). Precies `image` + `alt` als tekst, en verder alleen bekende sleutels.
 */
export function isMediaShape(v: unknown): v is Media {
  if (!isRecord(v)) return false
  if (typeof v.image !== 'string' || typeof v.alt !== 'string') return false
  return Object.keys(v).every((k) => ALLOWED_KEYS.has(k))
}

const clampPct = (n: number) => Math.min(100, Math.max(0, Math.round(n)))

/** CSS `object-position` voor de uitsnede; zonder focus het midden. */
export const focusPosition = (focus?: MediaFocus): string | undefined => (focus ? `${clampPct(focus.x)}% ${clampPct(focus.y)}%` : undefined)

/** Focus uit een klik of pijltjestoets, altijd binnen 0–100. */
export const clampFocus = (f: MediaFocus): MediaFocus => ({ x: clampPct(f.x), y: clampPct(f.y) })

/**
 * `srcset` voor een foto uit de eigen bibliotheek; `null` voor elke andere link
 * (die gaat via `ProductImage` met zijn eigen terugval).
 */
export function librarySrcSet(url: string): string | null {
  const m = LIBRARY.exec(url)
  if (!m) return null
  return MEDIA_WIDTHS.map((w) => `${m[1]}${w}.webp ${w}w`).join(', ')
}

/**
 * Wat er mis is met een beeld, of `null`. Dezelfde regels als de server; leeg beeld mag
 * (dan de standaardfoto), maar dan ook geen video.
 */
export function mediaProblem(v: unknown): string | null {
  if (!isRecord(v)) return 'Dit beeld kon niet worden gelezen.'
  if (typeof v.image !== 'string' || typeof v.alt !== 'string') return 'Dit beeld kon niet worden gelezen.'
  if (v.image && !SAFE_SRC.test(v.image)) return 'De foto moet een link zijn die begint met https://.'
  if (v.alt.length > MEDIA_ALT_MAX) return `De beschrijving is te lang (hoogstens ${MEDIA_ALT_MAX} tekens).`
  if (v.focus !== undefined) {
    const f = v.focus
    const ok = isRecord(f) && [f.x, f.y].every((n) => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 100)
    if (!ok) return 'Het belangrijkste punt van de foto valt buiten het beeld.'
  }
  if (v.video !== undefined) {
    const vid = v.video
    if (!isRecord(vid) || typeof vid.src !== 'string' || !SAFE_VIDEO.test(vid.src)) return 'De video moet een link zijn die begint met https://.'
    if (!v.image) return 'Kies ook een foto: die staat er zolang de video laadt, en voor wie beweging uit heeft staan.'
    for (const key of ['poster', 'small'] as const) {
      const x = vid[key]
      if (x !== undefined && (typeof x !== 'string' || !(key === 'poster' ? SAFE_SRC : SAFE_VIDEO).test(x))) return 'De video kon niet worden gelezen.'
    }
  }
  return null
}

/**
 * De galerij van een product: altijd eerst de packshot (`image`, die ook in de mand, op kaarten en
 * in Google staat), daarna de extra beelden. Een oudere opslag zonder `media` = alleen de packshot.
 */
export function productGallery(p: { image: string; imageAlt?: string; media?: Media[] }, mainAlt: string): Media[] {
  const extra = (p.media ?? []).filter(hasMedia).slice(0, PRODUCT_MEDIA_MAX)
  return [{ image: p.image, alt: p.imageAlt?.trim() || mainAlt }, ...extra]
}
